import { Fragment } from 'react';
import {
  AnyRendererProps,
  FieldType,
  GremlinPathItem,
  RenderOptions,
} from './types';
import { APIEntityType, ArdoqId, UserDataForRenderers } from '@ardoq/api-types';
import {
  ArdoqIcon,
  ArrowForwardIcon,
  getIcon,
  getIcons,
  getShapes,
  IconSize,
} from '@ardoq/icons';
import { getFieldLabel, getFieldType } from './utils/fields';
import { EnhancedScopeDataWithBranchName } from './enhanceScopeData';
import {
  Any,
  Cardinality,
  Checkbox,
  Color,
  ComponentType,
  ComponentTypeChildren,
  ComponentTypeList,
  Date,
  DateRange,
  DefaultValueAttribute,
  Description,
  FileRenderer,
  Icon,
  Image,
  MultiSelect,
  Nonexistent,
  Number,
  NumberFormatOptionsRender,
  Parent,
  ProvenanceRenderer,
  ReferenceType,
  ReferenceTypeLine,
  ReferenceTypeList,
  RootWorkspace,
  Shape,
  TaggedEntityList,
  TextWithOptions,
  Url,
  User,
  WorkspaceModel,
} from './TypeRenderers';
import { readRawValue } from './utils/readRawValue';
import { EntityRepresentation } from '../DiffMergeTable/EntityRepresentation/EntityRepresentation';
import { FlexBox } from '@ardoq/layout';
import { colors } from '@ardoq/design-tokens';
import { ComponentLinkHandler } from '../DiffMergeTable/EntityRepresentation/ComponentRepresentationWithLink';
import { EnhancedScopeData } from '@ardoq/data-model';
import type { I18nLocale } from '@ardoq/i18n';

export const graphics = {
  shapes: getShapes(),
  icons: { ...getIcons(), none: getIcon('') } as Record<string, ArdoqIcon>,
};

type EntityRenderProps = {
  entityId: ArdoqId | number;
  fieldName: string;
  enhancedScopeData: EnhancedScopeDataWithBranchName | EnhancedScopeData;
  users: Record<ArdoqId, UserDataForRenderers>;
  options: RenderOptions;
  i18nLocale?: I18nLocale;
};

type GremlinPathRendererProps = {
  gremlinPathArray: GremlinPathItem[];
  enhancedScopeData: EnhancedScopeDataWithBranchName;
  getComponentLinkHandler?: (
    _id: ArdoqId,
    workspaceId?: ArdoqId
  ) => ComponentLinkHandler | undefined;
  hideDefaultPopover?: boolean;
  underlineOnHover?: boolean;
  variant?: 'default' | 'light';
  i18nLocale?: I18nLocale;
};

export const GremlinPathRenderer = ({
  gremlinPathArray,
  enhancedScopeData,
  getComponentLinkHandler,
  hideDefaultPopover,
  underlineOnHover,
  variant,
  i18nLocale,
}: GremlinPathRendererProps) => (
  <FlexBox gap="xsmall" align="center">
    {gremlinPathArray.map(
      ({ _id, ardoq: { 'entity-type': entityType } }, index) => (
        <Fragment key={index}>
          {index !== 0 ? (
            <ArrowForwardIcon
              style={{ color: colors.grey500 }}
              iconSize={IconSize.MEDIUM}
            />
          ) : null}
          {entityType === APIEntityType.REFERENCE ? (
            <i>
              <ReferenceType
                entityId={_id}
                enhancedScopeData={enhancedScopeData}
                i18nLocale={i18nLocale}
              />
            </i>
          ) : (
            <EntityRepresentation
              shouldHaveWrapper
              entityId={_id}
              {...{ entityType, graphics }}
              enhancedScopeData={enhancedScopeData}
              componentLinkHandler={getComponentLinkHandler?.(
                _id,
                enhancedScopeData?.componentsById[_id]?.rootWorkspace
              )}
              hideDefaultPopover={hideDefaultPopover}
              underlineOnHover={underlineOnHover}
              variant={variant}
              linkColor={variant === 'light' ? colors.textModerate : undefined}
              i18nLocale={i18nLocale}
            />
          )}
        </Fragment>
      )
    )}
  </FlexBox>
);

export const ComponentRenderer = (props: EntityRenderProps) => (
  <Renderer
    entityType={APIEntityType.COMPONENT}
    graphics={graphics}
    {...props}
  />
);

export const ReferenceRenderer = (props: EntityRenderProps) => (
  <Renderer
    entityType={APIEntityType.REFERENCE}
    graphics={graphics}
    {...props}
  />
);

export const FieldPropertyRenderer = (props: EntityRenderProps) => (
  <Renderer entityType={APIEntityType.FIELD} graphics={graphics} {...props} />
);

type EntityWithParentRendererProps = EntityRenderProps & {
  parentEntityId: ArdoqId;
};

export const ComponentTypeRenderer = (props: EntityWithParentRendererProps) => (
  <Renderer
    entityType={APIEntityType.COMPONENT_TYPE}
    graphics={graphics}
    {...props}
  />
);

export const ReferenceTypeRenderer = (props: EntityWithParentRendererProps) => (
  <Renderer
    entityType={APIEntityType.REFERENCE_TYPE}
    graphics={graphics}
    {...props}
  />
);

export const Renderer = (props: AnyRendererProps) => {
  const {
    entityType,
    entityId,
    fieldName,
    enhancedScopeData,
    parentEntityId,
    options,
  } = props;

  const fieldType = getFieldType(
    entityType,
    entityId,
    fieldName,
    enhancedScopeData
  );

  /**
   * Pay attention here:
   * The expected behaviour is:
   * for options = { rawValue: undefined } => value should be undefined
   * for options = { } => value should be read from readRawValue(...) fn
   */
  const value = Object.prototype.hasOwnProperty.call(options, 'rawValue')
    ? options.rawValue
    : readRawValue(
        entityType,
        entityId,
        fieldName,
        enhancedScopeData,
        parentEntityId
      );

  switch (fieldType) {
    case FieldType.CHECKBOX:
    case FieldType.GLOBAL:
    case FieldType.GLOBALREF:
    case FieldType.RETURNS_VALUE:
    case FieldType.FLEXIBLE:
    case FieldType.HAS_CARDINALITY:
      return <Checkbox {...props} value={value} />;

    case FieldType.COLOR:
      return <Color {...props} value={value} />;

    case FieldType.COMPONENT_TYPE_CHILDREN:
      return <ComponentTypeChildren {...props} value={value} />;

    case FieldType.CREATED_BY:
    case FieldType.LAST_MODIFIED_BY:
    case FieldType.LOCK:
    case FieldType.USER:
      return <User {...props} value={value} />;

    case FieldType.CREATED:
    case FieldType.DATE_ONLY:
    case FieldType.DATE_TIME:
    case FieldType.LAST_UPDATED:
      return <Date {...props} value={value} />;

    case FieldType.CREATED_PROVENANCE:
    case FieldType.LAST_PROVENANCE:
      return <ProvenanceRenderer {...props} value={value} />;

    case FieldType.DESCRIPTION:
      return <Description {...props} value={value} />;

    case FieldType.NAME:
    case FieldType.TEXT_AREA:
    case FieldType.TEXT:
    case FieldType.FIELD_DESCRIPTION:
    case FieldType.EMAIL:
      return <TextWithOptions {...props} value={value} />;

    case FieldType.ICON:
      return <Icon {...props} value={value} />;

    case FieldType.URL:
      return <Url {...props} value={value} />;

    case FieldType.IMAGE:
      return <Image {...props} value={value} />;

    case FieldType.MODEL:
      return <WorkspaceModel {...props} />;

    case FieldType.TARGET:
    case FieldType.SOURCE:
    case FieldType.PARENT:
      return <Parent {...props} value={value} />;

    case FieldType.ROOT_WORKSPACE:
    case FieldType.TARGET_WORKSPACE:
      return <RootWorkspace {...props} value={value} />;

    case FieldType.SELECT_MULTIPLE_LIST:
      return <MultiSelect {...props} value={value} />;

    case FieldType.SHAPE:
      return <Shape {...props} value={value} />;

    case FieldType.TYPE_ID_COMPONENT:
    case FieldType.TYPE_NAME_COMPONENT:
      return <ComponentType {...props} />;

    case FieldType.TYPE_ID_REFERENCE:
      return <ReferenceType {...props} />;

    case FieldType.DATE_ONLY_RANGE:
    case FieldType.DATE_TIME_RANGE:
      return <DateRange {...props} value={value} />;

    case FieldType.COMPONENT_TYPE:
      return <ComponentTypeList {...props} />;

    case FieldType.REFERENCE_TYPE:
      return <ReferenceTypeList {...props} />;

    case FieldType.DEFAULT_VALUE:
    case FieldType.DEFAULT_VALUE_DATE_ONLY_RANGE:
    case FieldType.DEFAULT_VALUE_DATE_TIME_RANGE:
      return <DefaultValueAttribute {...props} />;

    case FieldType.REFERENCE_TYPE_LINE:
    case FieldType.REFERENCE_TYPE_LINE_BEGINNING:
    case FieldType.REFERENCE_TYPE_LINE_ENDING:
      return <ReferenceTypeLine {...props} />;

    case FieldType.NUMBER:
      return <Number {...props} value={value} />;

    case FieldType.NUMBER_FORMAT_OPTIONS:
      return <NumberFormatOptionsRender {...props} />;

    case FieldType.COMPONENTS_LIST:
    case FieldType.REFERENCE_LIST:
      return <TaggedEntityList {...props} value={value} />;

    case FieldType.TARGET_CARDINALITY:
    case FieldType.SOURCE_CARDINALITY:
      return <Cardinality {...props} value={value} />;

    case FieldType.FILE:
      return <FileRenderer {...props} value={value} />;

    case FieldType.NONE:
      return <Nonexistent i18nLocale={props.i18nLocale} />;

    default:
      return <Any {...props} value={value} />;
  }
};

export const FieldLabelRenderer = ({
  fieldName,
  enhancedScopeData,
  entityType,
  i18nLocale,
}: Pick<
  AnyRendererProps,
  'fieldName' | 'enhancedScopeData' | 'entityType' | 'i18nLocale'
>) => (
  <>
    {getFieldLabel({
      fieldName,
      enhancedScopeData,
      entityType,
      i18nLocale,
    })}
  </>
);
